import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { Form } from 'grab-ui';
import EntityStepFormModal from './common/EntityStepFormModal';
import AddCampaignChannelForm from './AddCampaignChannelForm';
import AddCampaignDetailsForm from './AddCampaignDetailsForm';
import { hasJustFail, hasJustSuccess, isRequestsPending } from 'utils/request-state-utils';
import { CREATE_UCD_CAMPAIGN } from 'omega-rtc/actions/ucd';
import { UCD_ERRORS, UCD_ROUTES_LINK } from 'omega-rtc/constants/ucd';
import { getRequestStateKey } from 'omega-rtc/utils/ucd-utils';
import { snackBar } from 'omega-rtc/components/custom/SnackBar';

type AddCampaignFormProps = {
  closeModal: Function,
  createCampaign: Function,
  form: any,
  configs: any,
  countries: Object,
  user: Object,
  router: any,
  project: Object,
};

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const AddCampaignForm = (props: AddCampaignFormProps) => {
  const { form, closeModal } = props;
  const formProps = {
    layout: 'vertical',
  };

  const createUcdCampaignRequest = useSelector(state =>
    _.get(state.requests, getRequestStateKey(CREATE_UCD_CAMPAIGN), {})
  );
  const newlyCreatedCampaign = useSelector(state => state.ucd.newlyCreatedCampaign);
  const createCampaignError = useSelector(state => state.ucd.createCampaignError);

  const createCampaignRequestPending =
    createUcdCampaignRequest && isRequestsPending(createUcdCampaignRequest);
  const lastRequestState = usePrevious(createUcdCampaignRequest);
  useEffect(() => {
    if (
      createUcdCampaignRequest &&
      lastRequestState &&
      hasJustSuccess(lastRequestState, createUcdCampaignRequest)
    ) {
      const { project, campaignDescription } = form.getFieldsValue();
      snackBar.success({ content: `${campaignDescription} created successfully` });
      const projectID = props.project
        ? props.project.projectID
        : project.projectID;
      props.router.push(
        UCD_ROUTES_LINK.CAMPAIGN_VIEW(projectID, newlyCreatedCampaign),
      );
    } else if (
      createUcdCampaignRequest &&
      lastRequestState &&
      hasJustFail(lastRequestState, createUcdCampaignRequest)
    ) {
      const showAlert = _.toLower(createCampaignError).includes(
        UCD_ERRORS.CAMPAIGN_CODE_EXIST.errorMessage,
      );
      form.setFieldsValue({ showCampaignCodeExistsAlert: showAlert });
    }
  }, [createUcdCampaignRequest]);

  const [channelGroup, setChannelGroup] = useState('');
  const isNextAllowedFunction = index => {
    if (index === 1) {
      const { project, channelGroup } = form.getFieldsValue();
      if (!project) {
        snackBar.error({ content: 'Project is mandatory.' });
        return false;
      }
      if (!channelGroup) {
        snackBar.error({ content: 'Channel Group is mandatory.' });
        return false;
      }
    } else if (index === 2) {
      const { channelType } = form.getFieldsValue();
      if (!channelType || channelType.length === 0) {
        snackBar.error({ content: 'Channel Type is mandatory.' });
        return false;
      }
    }
    return true;
  };

  const createCampaign = status => {
    form.setFieldsValue({ showCampaignCodeExistsAlert: false });
    form.validateFields((err, values) => {
      if (!err) {
        const {
          budgetType,
          vertical,
          project,
          startDate,
          endDate,
          channelType,
          campaignDescription,
          channelGroup,
          campaignName,
          campaignCode,
          timezone,
        } = values;

        if (startDate.diff(endDate) > 0) {
          snackBar.error({ content: 'Start Date is after end date' });
          return;
        }

        const requestBody = {
          campaign: {
            shortName: campaignName,
            name: campaignDescription,
            start: startDate.format('YYYY-MM-DD'),
            end: endDate.format('YYYY-MM-DD'),
            startTime: startDate.clone().utc(),
            endTime: endDate.clone().utc(),
            timezone,
            channels: channelType,
            budgetSource: budgetType,
            channelGroup,
            code: campaignCode,
            verticals: [vertical],
          },
          status,
          user: props.user.email,
        };
        props.createCampaign(requestBody, project.projectID);
      } else {
        snackBar.error({ content: 'Invalid input provided' });
      }
    });
  };

  let isCampaignDetailFilled = false;
  if (form && form.getFieldsValue) {
    const {
      vertical,
      startDate,
      endDate,
      channelType,
      campaignDescription,
      campaignName,
    } = form.getFieldsValue();
    isCampaignDetailFilled =
      vertical &&
      startDate &&
      endDate &&
      channelType &&
      campaignDescription &&
      campaignName;
  }

  const stepModalProps = {
    closeModal,
    title: 'Add New Campaign',
    size: 'small',
    onSaveDraft: () => {
      createCampaign('DRAFT');
    },
    onCreate: () => {
      createCampaign('DRAFT');
    },
    formProps,
    FormComponent: Form,
    isNextAllowedFn: isNextAllowedFunction,
    publishLoading: createCampaignRequestPending,
    saveDraftLoading: createCampaignRequestPending,
    publishDisabled: !isCampaignDetailFilled,
    mapStepComponent: [
      {
        name: 'Channel',
        component: (
          <AddCampaignChannelForm
            project={props.project}
            form={form}
            value={channelGroup}
            onChannelGroupChange={setChannelGroup}
          />
        ),
      },
      {
        name: 'Details',
        component: (
          <AddCampaignDetailsForm
            router={props.router}
            countries={props.countries}
            configs={props.configs}
            form={form}
            channelGroup={channelGroup}
          />
        ),
      },
    ],
  };
  return <EntityStepFormModal {...stepModalProps} />;
};

export default Form.create()(AddCampaignForm);
